import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Eye, Download, Users, Clock, TrendingUp, Calendar, FileText, BarChart3 } from 'lucide-react';
import fileService from '../services/fileService';
import toast from 'react-hot-toast';
import { CardLoader } from './CommonLoader';

const FileStatistics = ({ isOpen, onClose, file, dataroomUuid }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && file) {
      fetchStatistics();
    }
  }, [isOpen, file]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fileService.getFileStatistics(dataroomUuid, file.uuid);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch file statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">File Statistics</h2>
            <p className="text-sm text-gray-500 mt-1">{file?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <CardLoader />
          ) : stats ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Views</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {stats.total_views || 0}
                      </p>
                    </div>
                    <Eye className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Downloads</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {stats.total_downloads || 0}
                      </p>
                    </div>
                    <Download className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Unique Users</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">
                        {stats.unique_users || 0}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Detailed Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  File Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">File Type</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {file?.mime_type || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">File Size</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {fileService.formatFileSize(file?.size || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Uploaded On</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatDate(file?.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Modified</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatDate(file?.updated_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Access Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Access Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">First Viewed</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatTime(stats.first_viewed_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Viewed</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatTime(stats.last_viewed_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">First Downloaded</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatTime(stats.first_downloaded_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Downloaded</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formatTime(stats.last_downloaded_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Activity Trends */}
              {stats.activity_by_day && stats.activity_by_day.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Activity Trends
                  </h3>
                  <div className="space-y-2">
                    {stats.activity_by_day.map((day, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{day.date}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-blue-600">
                            {day.views} views
                          </span>
                          <span className="text-sm text-green-600">
                            {day.downloads} downloads
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Users */}
              {stats.top_users && stats.top_users.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Most Active Users
                  </h3>
                  <div className="space-y-3">
                    {stats.top_users.map((user, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-blue-600">
                            {user.view_count} views
                          </span>
                          <span className="text-sm text-green-600">
                            {user.download_count} downloads
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <BarChart3 className="mx-auto h-16 w-16 text-gray-300" />
              <p className="mt-4 text-gray-500">No statistics available</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FileStatistics;